import { useId, useMemo } from 'react'
import type { SettingLookCode } from '@gk/contracts'
import { drawLook, genericShape, type LookDrawing, type LookFrame, type LookGrid } from '../../data/lookGeometry.ts'

/** M25-P (docs/M25.md A, E, rule 5; ADR-129): ONE PICTURE of the library — the app's own inline SVG, drawn from `data/lookGeometry.ts` on the generic
 *  shape of the area (round, block, strip: from the area type codes, `frameFor`), in the area's own clock (12 the back of the green, 6 where the play
 *  arrives). Stroke and fill in currentColor only: the theme's token and its dark variant come from the text colour the page puts around it
 *  (`text-brand dark:text-fairway`, …). Decorative (aria-hidden, no letter inside): the club's word beside it carries the meaning. A code this build
 *  does not know draws nothing. `size`: the 24-grid pictogram (28 px by default; the strips and chips set 1 em) or the 48-grid schema (56 px). */
export function LookPicture({ code, frame = 'round', size = 'picto', className, testId }: { code: string; frame?: LookFrame; size?: 'picto' | 'schema'; className?: string; testId?: string }) {
  const grid: LookGrid = size === 'picto' ? 24 : 48
  const drawing = useMemo(() => drawLook(code, genericShape(frame, grid), grid), [code, frame, grid])
  const clipId = useClipId()
  if (!drawing) return null
  return (
    <svg viewBox={`0 0 ${grid} ${grid}`} fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"
      className={className ?? (size === 'picto' ? 'size-7 shrink-0' : 'size-14 shrink-0')} data-look={code} data-frame={frame} data-testid={testId}>
      <LookMarks drawing={drawing} clipId={clipId} />
    </svg>
  )
}

/** A clipPath id unique on the page and usable in `url(#…)` as it is (React's own ids carry characters a URL fragment would need escaped). */
export function useClipId(): string {
  return `gk-look-${useId().replace(/[^A-Za-z0-9_-]/g, '')}`
}

/** The marks of a drawing as SVG paths, in currentColor; the bands clipped to the shape (even-odd: a hole stays a hole). */
export function LookMarks({ drawing, clipId }: { drawing: LookDrawing; clipId: string }) {
  const clipped = drawing.marks.some((m) => m.clip)
  return (
    <>
      {clipped && <defs><clipPath id={clipId}><path d={drawing.clip} clipRule="evenodd" /></clipPath></defs>}
      {drawing.marks.map((m, i) => m.fill !== undefined
        ? <path key={i} d={m.d} fill="currentColor" fillOpacity={m.fill} stroke="none" fillRule={m.evenodd ? 'evenodd' : undefined} clipPath={m.clip ? `url(#${clipId})` : undefined} data-mark={m.role} />
        : <path key={i} d={m.d} fill="none" stroke="currentColor" strokeWidth={m.width} strokeOpacity={m.opacity} strokeDasharray={m.dash} data-mark={m.role} />)}
    </>
  )
}

/** The i18n key of a picture's name (`looks.<code>`, packages/i18n/parts/m25-p.json), for the library's tiles in the set-up. */
export const lookNameKey = (code: SettingLookCode): string => `looks.${code}`
